<template>
  <section class="p-6 flex flex-col gap-6">
    <!-- Breadcrumb -->
    <nav class="text-gray-400 text-sm">
      <span class="font-semibold cursor-pointer" @click="$router.push('/')">
        Home
      </span>
      <span class="mx-2">/</span>
      <span class="text-gray-200 font-bold">Contact Messages</span>
    </nav>

    <h3 class="font-bold text-2xl text-white">Contact Messages</h3>

    <!-- Search & Filter -->
    <div class="flex items-center justify-between gap-4">
      <input
        v-model="search"
        @keyup.enter="fetchMessages"
        placeholder="Search by name, email, or subject..."
        class="border border-gray-600 bg-gray-800 text-gray-100 rounded-md px-4 py-2 w-1/2"
      />

      <select
        v-model="filterStatus"
        class="border border-gray-600 bg-gray-800 text-gray-100 rounded-md px-3 py-2"
      >
        <option value="all">All</option>
        <option value="unread">Unread</option>
        <option value="read">Read</option>
      </select>
    </div>

    <!-- Table -->
    <div class="overflow-x-auto">
      <table class="min-w-full border-collapse bg-gray-900 rounded-lg shadow">
        <thead>
          <tr class="bg-gray-800 text-left text-sm font-semibold text-gray-200">
            <th class="px-4 py-2">Name</th>
            <th class="px-4 py-2">Email</th>
            <th class="px-4 py-2">Subject</th>
            <th class="px-4 py-2">Message</th>
            <th class="px-4 py-2">Status</th>
            <th class="px-4 py-2">Created At</th>
            <th class="px-4 py-2 text-center">Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="msg in filteredMessages"
            :key="msg.id"
            class="border-t border-gray-800 hover:bg-gray-800 text-sm text-gray-100"
          >
            <!-- Name -->
            <td class="px-4 py-2">
              <div class="font-semibold">
                {{ msg.name }}
              </div>
            </td>

            <!-- Email -->
            <td class="px-4 py-2">
              <a
                :href="`mailto:${msg.email}`"
                class="text-blue-400 hover:underline"
              >
                {{ msg.email }}
              </a>
            </td>

            <!-- Subject -->
            <td class="px-4 py-2 max-w-xs">
              <span class="block truncate">
                {{ msg.subject || '-' }}
              </span>
            </td>

            <!-- Message -->
            <td class="px-4 py-2 max-w-md">
              <span class="block truncate">
                {{ msg.message || '-' }}
              </span>
            </td>

            <!-- Status -->
            <td class="px-4 py-2">
              <span
                class="px-2 py-1 text-xs rounded-full"
                :class="msg.is_read ? 'bg-green-900 text-green-300' : 'bg-yellow-900 text-yellow-300'"
              >
                {{ msg.is_read ? 'Read' : 'Unread' }}
              </span>
            </td>

            <!-- Created At -->
            <td class="px-4 py-2 whitespace-nowrap">
              {{ formatDate(msg.created_at) }}
            </td>

            <!-- Actions -->
            <td class="px-4 py-2 text-center whitespace-nowrap">
              <button
                class="text-blue-400 hover:underline mx-1"
                @click="viewMessage(msg)"
              >
                View
              </button>

              <button
                class="text-indigo-400 hover:underline mx-1"
                @click="toggleRead(msg)"
              >
                {{ msg.is_read ? 'Mark as unread' : 'Mark as read' }}
              </button>

              <button
                class="text-red-400 hover:underline mx-1"
                @click="deleteMessage(msg.id)"
              >
                Delete
              </button>
            </td>
          </tr>

          <tr v-if="!loading && filteredMessages.length === 0">
            <td colspan="7" class="text-center py-4 text-gray-500">
              No contact messages found.
            </td>
          </tr>

          <tr v-if="loading">
            <td colspan="7" class="text-center py-4 text-gray-400">
              Loading...
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import apiClient from "@/apiClient";

const messages = ref([]);
const search = ref("");
const filterStatus = ref("all"); // all | unread | read
const loading = ref(false);

function formatDate(date) {
  if (!date) return "-";
  return new Date(date).toLocaleString();
}

async function fetchMessages() {
  try {
    loading.value = true;
    const res = await apiClient.get("/contact-messages");
    const payload = res.data;

    // متوقّع من الـ API:
    // {
    //   success: true,
    //   data: { messages: [...] }
    // }
    if (payload && payload.data && Array.isArray(payload.data.messages)) {
      messages.value = payload.data.messages;
    } else if (Array.isArray(payload)) {
      messages.value = payload;
    } else {
      messages.value = [];
    }
  } catch (e) {
    console.error("Error fetching contact messages:", e);
    messages.value = [];
  } finally {
    loading.value = false;
  }
}

const filteredMessages = computed(() => {
  let list = messages.value;

  // فلترة Read / Unread
  if (filterStatus.value === "unread") {
    list = list.filter((m) => !m.is_read);
  } else if (filterStatus.value === "read") {
    list = list.filter((m) => m.is_read);
  }

  // فلترة البحث
  if (!search.value.trim()) return list;

  const q = search.value.toLowerCase();
  return list.filter((m) => {
    const name = String(m.name || "").toLowerCase();
    const email = String(m.email || "").toLowerCase();
    const subject = String(m.subject || "").toLowerCase();
    const message = String(m.message || "").toLowerCase();

    return (
      name.includes(q) ||
      email.includes(q) ||
      subject.includes(q) ||
      message.includes(q)
    );
  });
});

function viewMessage(msg) {
  alert(
    `From: ${msg.name} <${msg.email}>\n\nSubject: ${msg.subject || "-"}\n\nMessage:\n${msg.message || "-"}`
  );
}

async function toggleRead(msg) {
  try {
    const newStatus = !msg.is_read;

    await apiClient.post(`/contact-messages/${msg.id}/read-status`, {
      is_read: newStatus,
    });

    msg.is_read = newStatus;
  } catch (error) {
    console.error("Error updating read status:", error);
    alert("Failed to update read status.");
  }
}

async function deleteMessage(id) {
  if (!confirm("Are you sure you want to delete this message?")) return;

  try {
    await apiClient.delete(`/contact-messages/${id}`);
    messages.value = messages.value.filter((m) => m.id !== id);
  } catch (e) {
    console.error(e);
    alert("Failed to delete message.");
  }
}

onMounted(() => {
  fetchMessages();
});
</script>

<style scoped>
table {
  border-radius: 0.5rem;
  overflow: hidden;
}
</style>
